import React, {ForwardedRef, useImperativeHandle, useMemo, useRef} from 'react';
import { LIGHT_COLOR_ARR, LIGHT_RGBA_COLORS } from '../_config/_constant';
import {ChartRef, PieChartProps} from '../_config/_interface';
import Chart from '..';
import {useDblIntl} from '@/pages/datalab/utils/utils';
import {useHoverPieOptions} from '@customComp/Chart/_config/_hooks';
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";
import {changeLegendLocation} from "@customComp/Chart/chartConfigs/legend/legendUtils";
import { isEmpty } from 'lodash-es';

// 饼图数据项类型
interface IPieChartDataItem {
  [metricName: string]: Array<{
    dimension_name: string;
    value: {
      value: string;
      value_type?: 'percent' | 'number';
      percent?: string;
    };
  }>;
}

// 饼图图例配置类型
interface IPieChartLegend {
  data: string[];
  left: number | 'center';
  bottom: number;
  type: 'scroll';
  icon: 'circle';
  itemHeight: number;
  itemWidth: number;
  inactiveBorderWidth: number;
  itemGap: number;
  pageIconColor: string;
  pageIconInactiveColor: string;
  pageIconSize: number;
  pageButtonPosition: 'end';
  textStyle: {
    color: string;
    fontFamily: string;
    fontSize: number;
    lineHeight: number;
    padding: number[];
  };
}

// 饼图标题配置类型
interface IPieChartTitle {
  text: string;
  top: string;
  left: string;
  textAlign: 'center';
  textVerticalAlign: 'middle';
  padding: number;
  textStyle: {
    rich: {
      fontSize: number;
      fontWeight: number;
      color: string;
      align: string;
      overflow: string;
    };
  };
}

// 饼图系列数据项类型
interface IPieSeriesDataItem {
  name: string;
  value: string | number;
  percent?: string;
  isPercent: boolean;
  itemStyle?: {
    color?: string;
  };
}

// 饼图标签格式化参数类型
interface IPieLabelFormatterParams {
  name: string;
  data: {
    percent: string;
    value: string | number;
  };
}

// 饼图系列配置类型
interface IPieSeriesOption {
  name: string;
  type: 'pie';
  data: IPieSeriesDataItem[];
  radius: number[];
  center: string[];
  label: {
    show: boolean;
    alignTo: 'none';
    formatter: (item: IPieLabelFormatterParams) => string;
    padding: number[];
    rich: Record<string, {
      padding: number[];
      align: string;
      opacity: number;
      width?: string;
      height?: number;
      borderWidth?: number;
      borderColor?: string;
      lineHeight?: number;
      color?: string;
    }>;
    fontSize: number;
    color: string;
    minMargin: number;
    edgeDistance: number;
  };
  labelLine: {
    show: boolean;
    length: number;
    lineStyle: {
      width: number;
    };
  };
  itemStyle: {
    borderWidth: number;
    borderColor: string;
    borderRadius: number;
  };
  width: string;
  left: string | number;
  top: number;
}

const PieChartInner: React.ForwardRefRenderFunction<ChartRef, PieChartProps> = (props, ref: ForwardedRef<ChartRef>) => {
  const { chartData: pieChartData, curMetrics, showLegends = true, pieChartTitle, metricInfo, chartConfig } = props;
  const { isEn } = useDblIntl('chart');
  const chartRef = useRef<ChartRef>(null);
  // 获取实例
  const instance = () => chartRef?.current?.instance();
  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance
  }));
  // 判断计算小数位数是为了兼容旧数据
  const decimal = ['float', 'percent'].includes(metricInfo?.column_date_type) ? metricInfo?.accuracy : 0;
  // 图例
  const legends = useMemo(() => {
    const legends: IPieChartLegend[] = [];
    (pieChartData || []).forEach((item: IPieChartDataItem, itemIndex: number) => {
      const legend: string[] = [];
      (item[curMetrics] || []).forEach((data) => {
        legend.push(data.dimension_name);
      });
      legends.push({
        data: legend,
        left: pieChartData.length > 1 ? 56 : 'center',
        bottom: (pieChartData.length - itemIndex - 1) * 20 - 7,
        type: 'scroll',
        icon: 'circle', // 修改icon形状
        itemHeight: 10,
        itemWidth: 10,
        inactiveBorderWidth: 0, // 图例关闭时icon圆点的描边粗细
        itemGap: 20, // 修改每个图例的间距
        pageIconColor: '#444444', // 翻页下一页的三角按钮颜色
        pageIconInactiveColor: '#888888', // 翻页（即翻页到头时）
        pageIconSize: 12, // 翻页按钮大小
        pageButtonPosition: 'end', // start
        textStyle: {
          // 图例文字的样式
          color: '#838697',
          fontFamily: 'Roboto',
          fontSize: 12,
          lineHeight: 16,
          padding: [-1, 0, 0, 0],
          // rich: {
          //   a: {
          //     verticalAlign: 'bottom',
          //   }
          // }
        },
      });
    });
    return legends;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMetrics, pieChartData]);

  const titles: IPieChartTitle[] = useMemo(() => {
    const titles: IPieChartTitle[] = [];
    (pieChartData || []).forEach((item: IPieChartDataItem, itemIndex: number) => {
      // const top = pieChartData.length === 1 ? '43%' : '45%';
      let left = '75%';
      if (pieChartData.length === 1) {
        left = '50%';
      } else if (itemIndex === 0) {
        left = '25%';
      }

      // const name = pieChartData.length === 1 ? '' : itemIndex === 0 ? `${dblFormatMessage('compare')}1` : `${dblFormatMessage('compare')}2`;
      // const nameSize = pieChartData.length === 1 ? 20 : 14;
      const isPercentValue = item?.[curMetrics]?.[0]?.value?.value?.includes?.('%');
      let total = 0;
      (item[curMetrics] || []).forEach((data) => {
        total = total + parseFloat(data?.value?.value || 0);
      });
      titles.push({
        text: formatDisplayValue({
          value: total,
          isPercent: isPercentValue,
          accuracy: metricInfo?.accuracy,
          unit: metricInfo?.unit,
        }),
        top: '49%',
        left,
        textAlign: 'center',
        textVerticalAlign: 'middle',
        padding: 0,
        textStyle: {
          rich: {
            fontSize: 12,
            fontWeight: 600,
            color: '#262832',
            // width: 100,
            align: 'center',
            overflow: 'truncate',
          },
        },
      });
    });
    return titles;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMetrics, pieChartData, isEn]);

  // 数据
  const series = useMemo(() => {
    const series: IPieSeriesOption[] = [];
    (pieChartData || []).forEach((item: IPieChartDataItem, itemIndex: number) => {
      const isPercentValue = item?.[curMetrics]?.[0]?.value?.value?.includes?.('%');
      const date: IPieSeriesDataItem[] = [];
      if (curMetrics) {
        (item[curMetrics] || []).forEach((data, dataIndex) => {
          date.push({
            name: data?.dimension_name,
            value: data?.value?.value?.split?.('%')?.[0] || Number('0').toFixed(decimal), // 兼容有%的字符串
            percent: data?.value?.percent,
            isPercent: data?.value?.value_type === 'percent',
          });
        });
      }
 
      let left = '50%';
      if (pieChartData.length === 1) {
        left = 'center';
      } else if (itemIndex === 0) {
        left = '0';
      }
      
      series.push({
        name: (pieChartTitle && pieChartTitle?.length>0) ? pieChartTitle[itemIndex] : '',
        type: 'pie',
        data: date,
        radius: [55, 95],
        // avoidLabelOverlap: false,
        center: ['50%', '50%'],
        // minAngle: 5,
        // padAngle: 5,
        label: {
          show: showLegends,
          alignTo: 'none',
          formatter: (item: IPieLabelFormatterParams) => {
            const {
              name,
              data: { percent, value },
            } = item;
            
            const text = formatDisplayValue({
              value,
              isPercent: isPercentValue,
              accuracy: metricInfo?.accuracy,
              unit: metricInfo?.unit,
            });
            
            return `{name|${name}}：{per|${text}(${percent})}\n{hr|}\n{a|}`;
          },
          padding: [0, -6],
          rich: {
            per: {
              padding: [4, 0],
              align: 'left',
              opacity: 1,
            },
            name: {
              padding: [4, 0],
              align: 'left',
              opacity: 1,
            },
            hr: {
              width: '100%',
              height: 0,
              borderWidth: 2,
              borderColor: 'auto',
              opacity: 1,
            },
            a: {
              lineHeight: 20,
              align: 'center',
              color: 'transparent',
            },
          },
          fontSize: 12,
          color: '#262832',
          minMargin: 5,
          edgeDistance: 10,
        },
        labelLine: {
          show: showLegends,
          length: 20,
          // length2: 30,
          // showAbove: false,
          // maxSurfaceAngle: 40,
          lineStyle: {
            width: 2,
          },
        },
        itemStyle: {
          borderWidth: 2,
          borderColor: '#fff',
          borderRadius: 4,
        },
        width: pieChartData.length > 1 ? '50%' : '100%',
        left,
        top: -16,
        // clockwise: false,
        // startAngle: 0, // 0是右侧, 180是左侧
      });
    });
    return series;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMetrics, pieChartData, pieChartTitle]);

  const option = useMemo(
    () => {
      const legendColors = chartConfig?.legendColors;
      const nextSeries = !isEmpty(legendColors)
        ? series.map((seriesItem) => {
            return {
              ...seriesItem,
              data: (seriesItem.data || []).map((dataItem, dataIndex) => {
                const legendKey = dataItem?.name ?? '';
                let color = legendColors?.[legendKey];
                if (!color) {
                  color = LIGHT_RGBA_COLORS[dataIndex % LIGHT_RGBA_COLORS.length] ?? 'rgba(80, 134, 243, 1)';
                }
            
                return {
                  ...dataItem,
                  itemStyle: {
                    ...(dataItem.itemStyle || {}),
                    color,
                  },
                };
              }),
            };
          })
        : series;

      const option = {
        // calculable: true,
        title: titles,
        grid: {
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          containLabel: true,
        },
        legend: legends,
        color: LIGHT_COLOR_ARR,
        tooltip: {
          // alwaysShowContent: true,
          confine: true,
          appendToBody: true,
          trigger: 'item',
          enterable: true, // 鼠标可进入提示框浮层中
          showDelay: 5, // 显示延迟，添加显示延迟可以避免频繁切换，单位ms
          // // hideDelay: 150, // 浮层隐藏的延迟，单位ms
          backgroundColor: '#262832',
          textStyle: {
            color: '#D8DAE4',
            fontSize: 12,
          },
          borderWidth: 0,
          formatter: (params: {data: {isPercent: boolean; value: number;}; seriesName: string; marker: string; name: string;}) => {
            
            const value = formatDisplayValue({
              value: params.data.value,
              isPercent: params.data.isPercent,
              accuracy: metricInfo?.accuracy,
              unit: metricInfo?.unit,
            });
            
            return `<div class='big-bar-chart'>
                    <div class="item-left">
                      <p class="series-name"></p>
                      <p class='series-name'>${params.marker}${params.name}</p>
                    </div>
                    <div class="item-right">
                      <p class="series-metric">${curMetrics?.replace(/(t[0-2]_)/, '') || ''}</p>
                      <p class="series-value">${value}</p>
                    </div>
                  </div>`;
          }
        },
        series: nextSeries,
      };
      changeLegendLocation({option, position: chartConfig.legendPosition});
      return option;
    },
    [series, chartConfig?.legendColors, chartConfig.legendPosition],
  );
  useHoverPieOptions(chartRef);
  
  return <div className="analysis-chart-container">
    <div className='analysis-chart-content'>
      <Chart ref={chartRef} option={option} notMerge={true} />
    </div>
  </div>;
};

const PieChart = React.forwardRef(PieChartInner);

export default PieChart;